import clsx from 'clsx';
import { ReactNode } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionHeaderAction,
  AccordionPanel,
} from '../Accordion';

interface Props {
  actions?: AccordionHeaderAction[];
  alignHeader?: 'center' | 'flex-start' | 'flex-end';
  children: ReactNode;
  className?: string;
  count?: number | null;
  customAction?: ReactNode;
  defaultExpanded?: boolean;
  focus?: boolean;
  header: string | ReactNode;
  icon?: ReactNode;
  inlinePadding?: number;
  loading?: boolean;
  maxWidth?: number | string;
  padding?: StandardSizes | 'none';
  TypographyProps?: import('@material-ui/core').TypographyProps;
}

export const CollapsableContent = ({
  actions,
  alignHeader = 'center',
  children,
  customAction = null,
  className,
  count = null,
  defaultExpanded = false,
  focus = false,
  header,
  icon = null,
  inlinePadding = 0,
  loading = false,
  maxWidth = '100%',
  padding = 'md',
  TypographyProps,
}: Props) => {
  const classes = useStyles();

  const renderIconCount = () => {
    if (icon && count !== null)
      return (
        <div className={classes.badge}>
          {count > 0 ? (
            <span className={classes.badgeCount}>{count}</span>
          ) : null}
          {icon}
        </div>
      );
    else if (icon && count === null)
      return <span className={classes.badge}>{icon}</span>;
    else if (icon === null && count !== null && count > 0)
      return <p className={classes.count}>{count}</p>;
    return;
  };

  const renderCustomAction = () => {
    if (customAction)
      return <div className={classes.customAction}>{customAction}</div>;
    return;
  };

  const renderHeader = () => {
    if (typeof header === 'string')
      return <h4 className={classes.headerText}>{header}</h4>;
    else return header;
  };

  return (
    <Accordion
      className={clsx(className, 'w-full', { [classes.focus]: focus })}
      defaultExpanded={defaultExpanded}
    >
      <AccordionHeader
        className={clsx(classes.header, classes.padding[padding])}
        actions={actions}
      >
        {renderIconCount()}
        {renderHeader()}
        {renderCustomAction()}
      </AccordionHeader>
      <AccordionPanel>{children}</AccordionPanel>
    </Accordion>
  );
};

const useStyles = () => ({
  badge: 'relative mr-5',
  badgeCount:
    'absolute -top-[6px] -right-[14px] flex items-center justify-center p-1 w-3 h-3 rounded-full text-[0.65rem] text-white font-extrabold bg-amber-200 text-amber-900 shadow-md',
  count: 'text-lg text-primary mr-5',
  customAction: 'ml-auto',
  focus: 'focus-within:border-l-primary focus-within:border-l',
  header: '!h-fit',
  headerText: 'text-[1rem]',
  padding: {
    none: 'py-0',
    sm: 'py-1',
    md: 'py-3',
    lg: 'py-4',
  },
});
